import { Injectable } from '@angular/core';
import { PermissionsService } from 'src/app/core/auth/permissions.service';
import { RequestTypeEnum } from '../enums/account-update.enum';
import { AccountWorkflowNavModel, SolicitationOptionModel } from '../models/workflow.models';
import { Permission } from 'src/app/core/clients/generated/client';

@Injectable({
  providedIn: 'root'
})
export class WorkflowNavigationService {

  constructor(private _permissionService: PermissionsService) { }

  get workflowNavList(): AccountWorkflowNavModel[] {
    return this._workflowNavList.filter(r => r.resolve ? r.resolve() : true);
  }

  get workflowSolicitationOptionsList(): SolicitationOptionModel[] {
    return this._workflowSilicationOptionsList;
  }

  /**
   * The request-type tiles, in the order they appear on the menu.
   *
   * ⚠️ **Display order is this array's order — NOT the `order` field.** Nothing sorts these:
   * `workflowNavList` filters, and the modal's `standardNavItems` / `recommendedNavItems` filter
   * again, so the template renders them in the sequence written here.
   *
   * 🔴 Despite its name, `order` is an **identity**: it is the `RequestTypeEnum` ordinal, which
   * the modal switches on to pick which wizard to render, and which the submit endpoints and CRM
   * read as the request type. Renumbering the enum to move a tile would repoint every one of
   * those. To reorder the menu, move the entries in this array.
   *
   * Each V2 tile sits directly beneath the v1 it will eventually replace, so the pair reads as
   * one choice with two vintages rather than as two unrelated requests.
   */
  _workflowNavList: AccountWorkflowNavModel[] = [
    {
      order: RequestTypeEnum.initialAccountSetup,
      // eslint-disable-next-line quotes
      title: 'Initial Account Setup',
      icon: 'account_balance',
      buttonClass: 'is-mint',
      isNew: true,
      // eslint-disable-next-line quotes
      newLabel: 'New!',
      // Opens the REALLOCATION V2 wizard in Initial Account mode — an initial account is a
      // reallocation with an empty current allocation, and one wizard files both.
      //
      // Gated on its OWN permission so it can be granted without also granting V2 reallocations.
      // The submit endpoint accepts either permission but checks the request type against it, so
      // holding this one cannot be used to file a type-2 reallocation.
      //
      // ⚠️ When this tile renders, the modal hides the Model Allocation / Trade Request tiles —
      // both of them, v1 and V2. See `standardNavItems` in account-update-main-modal.
      resolve: (): boolean => {
        return this._permissionService.hasOnePermission(Permission.SubmitInitialAccountRequest);
      },
    },
    {
      order: RequestTypeEnum.contribution,
      // eslint-disable-next-line quotes
      title: 'Contribute Funds',
      icon: 'attach_money',
      buttonClass: 'is-mint',
      resolve: (): boolean => {
        return this._permissionService.hasOnePermission(Permission.SubmitContributionRequest) && !this._permissionService.hasOnePermission(Permission.ViewOrionElementRequests);
      }
    },
    {
      order: RequestTypeEnum.contributionV2,
      // eslint-disable-next-line quotes
      title: 'Contribute Funds',
      description: 'Contribute funds to models, buy positions or set-aside cash.',
      icon: 'attach_money',
      buttonClass: 'is-mint',
      isNew: true,
      // V2 Contribution (ACMTECH-887). Opens the same wizard as Model Allocation / Trade
      // Request, in Contribution mode — the type rides on the payload, not on a second wizard.
      //
      // Its own permission so a pilot can enable contributions without distributions; the two
      // carry different risk and Close Account rides on the distribution one. Nothing holds it
      // until it is granted via RolePermissionV2, so the tile is hidden by default.
      //
      // ⚠️ "Contribute Funds" — the SAME title as the v1 tile above (AndyP, 2026-09-23). It was
      // briefly the noun "Contribution", naming the request type CRM files; the verb names what
      // the advisor is here to do, and someone scanning a menu is looking for their task, not for
      // a schema. The panel and the description carry the distinction the noun was making.
      //
      // 🔴 Safe ONLY because the modal compares the selected tile by `order`, never by title —
      // see the note on reallocationV2. Anywhere a title appears outside the panel has to say
      // which vintage it is; step 2's summary does that with the Guided pill, which keys on
      // `isNew` and so already covers all three of these.
      resolve: (): boolean => {
        return this._permissionService.hasOnePermission(Permission.SubmitContributionV2Request);
      },
    },
    {
      order: RequestTypeEnum.distribution,
      // eslint-disable-next-line quotes
      title: 'Distribute Funds',
      icon: 'payments',
      buttonClass: 'is-mint',
      resolve: (): boolean => {
        return this._permissionService.hasOnePermission(Permission.SubmitDistributionRequest) && !this._permissionService.hasOnePermission(Permission.ViewOrionElementRequests);
      }
    },
    {
      order: RequestTypeEnum.distributionV2,
      // eslint-disable-next-line quotes
      title: 'Distribute Funds / Close Account',
      description: 'Distribute funds from models, positions, cash or fully close the account.',
      icon: 'payments',
      buttonClass: 'is-mint',
      isNew: true,
      // V2 Distribution (ACMTECH-888), which also files Close Account via a checkbox on its
      // first step rather than as a separate tile or request type.
      //
      // 🟢 Hence the title naming BOTH jobs. Closing an account through a tile labelled
      // "Distribute Funds" was undiscoverable: the v1 menu has its own Close the Account tile,
      // so an advisor would reasonably use that and never find this path. This is the only
      // place the menu can say otherwise.
      //
      // ⚠️ So this ONE permission authorises closing an account, which the v1 menu splits
      // across SubmitDistributionRequest and SubmitAccountCloseRequest. Worth knowing before
      // granting it: an advisor with V2 distributions can close accounts even without the v1
      // close permission.
      resolve: (): boolean => {
        return this._permissionService.hasOnePermission(Permission.SubmitDistributionV2Request);
      },
    },
    {
      order: RequestTypeEnum.reallocation,
      // eslint-disable-next-line quotes
      title: 'Model Allocation / Trade Request',
      icon: 'device_hub',
      iconTransform: 'rotate(-90deg)',
      buttonClass: 'is-mint',
      // v1 workflow. Shown ALONGSIDE V2 rather than standing down for it, because V2 is a
      // pilot: an advisor granted the V2 permission needs v1 too, both as the fallback when
      // the new wizard does not suit the request in front of them and as something to
      // compare it against. Only the V2 permission decides whether V2 appears; nothing
      // hides v1.
      //
      // ⚠️ This title is IDENTICAL to the guided entry below. The two are told apart ONLY by the
      // guided-workflows panel that entry sits inside — there is no longer a " V2" suffix. See
      // the note on it before changing either title or the panel.
      resolve: (): boolean => {
        return this._permissionService.hasOnePermission(Permission.SubmitReallocationRequest);
      }
    },
    {
      order: RequestTypeEnum.reallocationV2,
      // eslint-disable-next-line quotes
      title: 'Model Allocations / Trades',
      description: 'Reallocate models and targets, trade positions or free up and set-aside cash.',
      icon: 'device_hub',
      iconTransform: 'rotate(-90deg)',
      buttonClass: 'is-mint',
      isNew: true,
      // V2 workflow (account-request-workflows-v2/reallocation), ACMTECH-902.
      //
      // Its own permission rather than SubmitReallocationRequest, so it can be granted per
      // pilot user. Nothing holds it until it is assigned via RolePermissionV2, so this
      // tile is hidden by default - but granting it now ADDS this tile rather than
      // replacing v1, which stays for the duration of the pilot.
      //
      // "Model Allocations / Trades", where v1 reads "Model Allocation / Trade Request". Both
      // halves plural because one request handles several of each, and no "Request" suffix
      // because every row on this menu is one — the word was spending the widest title's
      // remaining space saying nothing.
      //
      // 🔴 Still near-identical to the v1 entry above, and still deliberate: the panel and the
      // description are what tell them apart on screen.
      //
      // ⚠️ That is only safe because the modal now compares the highlighted tile by `order`
      // (`selectedWorkflowNavItem?.order === navItem.order`) rather than by title. It used to
      // compare titles, which is why the " V2" suffix was previously load-bearing: identical
      // titles highlighted BOTH rows when either was picked. Do not put that comparison back.
      //
      // ⚠️ Two rows can now read the same, so anywhere a title is shown OUTSIDE the panel has
      // to say which vintage it is. Step 2's summary does this with a Guided pill.
      resolve: (): boolean => {
        return this._permissionService.hasOnePermission(Permission.SubmitReallocationV2Request);
      },
    },
    {
      order: RequestTypeEnum.closeAccount,
      // eslint-disable-next-line quotes
      title: 'Close the Account',
      icon: 'not_interested',
      //buttonClass: 'is-warning',
      buttonClass: 'is-mint',
      resolve: (): boolean => {
        return this._permissionService.hasOnePermission(Permission.SubmitAccountCloseRequest) && !this._permissionService.hasOnePermission(Permission.ViewOrionElementRequests);
      }
    },
    {
      order: RequestTypeEnum.generalAccount,
      // eslint-disable-next-line quotes
      title: 'General Account Message / Request',
      icon: 'message',
      //buttonClass: 'is-warning',
      buttonClass: 'is-mint',
      resolve: (): boolean => {
        return this._permissionService.hasOnePermission(Permission.SubmitMessageFile);
      }
    },
  ];

  _workflowSilicationOptionsList: SolicitationOptionModel[] = [
    {
      // eslint-disable-next-line quotes
      title: `IAR initiated transaction request (Solicited)`,
      isSolicited: true,
      icon: 'account_circle',
      buttonClass: 'is-mint',
    },
    {
      // eslint-disable-next-line quotes
      title: `Client initiated transaction request (Unsolicited)`,
      isSolicited: false,
      icon: 'phone_callback',
      buttonClass: 'is-mint',
    }
  ];
}